import React, { FC, useRef } from 'react';
import classes from './LastTileCont.module.scss';
import KTileItem from './KTileItem';
import { KTileDTO } from '@envclient/envengine/src/envs/koenigsburg/KTile';
import { getTileHeight } from './KTileAsset';

interface LastTileContProps {
  lastTiles: KTileDTO[];
  selectedTileId: number | null;
  active: boolean;
  canvasRef: React.RefObject<HTMLDivElement>;
  zoom: number;
  onTileClick: (tile: KTileDTO, startStyle?: { left: number; top: number }) => void;
  onTileDragStart: (
    tile: KTileDTO,
    clientX: number,
    clientY: number,
    rect: DOMRect,
    sourceScale: number,
    pointerId: number
  ) => void;
}

const LastTileCont: FC<LastTileContProps> = ({
  lastTiles,
  selectedTileId,
  active,
  canvasRef,
  zoom,
  onTileClick,
  onTileDragStart
}) => {
  const tileRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  if (!active) return null;

  // canvas-local top/left of a tile in the stack (used to animate placement from its origin)
  const getStartStyle = (tileId: number): { left: number; top: number } | undefined => {
    const tileEl = tileRefs.current.get(tileId);
    if (!tileEl || !canvasRef.current) return undefined;
    const tileRect = tileEl.getBoundingClientRect();
    const canvasRect = canvasRef.current.getBoundingClientRect();
    return {
      left: (tileRect.left - canvasRect.left) / zoom,
      top: (tileRect.top - canvasRect.top) / zoom
    };
  };

  return (
    <div className={classes.tileCont}>
      {lastTiles.map((t, index) => {
        // dont render temp tiels - they are placed already
        if (t.isTemp) return null;
        return (
          <div
            key={t.id}
            ref={(el) => {
              if (el) tileRefs.current.set(t.id, el);
              else tileRefs.current.delete(t.id);
            }}
            style={{
              position: 'absolute',
              left: 0,
              bottom: index * 200,
              height: getTileHeight(t.shape),
              // own the touch so mobile browsers don't scroll/zoom instead of dragging
              touchAction: 'none'
            }}
            onClick={() => onTileClick(t, getStartStyle(t.id))}
            onPointerDown={(e) => {
              const tileEl = tileRefs.current.get(t.id);
              if (!tileEl) return;
              const rect = tileEl.getBoundingClientRect();
              // the tray (.tileCont) is CSS-scaled down; derive the rendered scale from the
              // post-transform height vs the inline (layout) height
              const sourceScale = rect.height / tileEl.offsetHeight || 1;
              onTileDragStart(t, e.clientX, e.clientY, rect, sourceScale, e.pointerId);
            }}>
            <KTileItem active={true} tile={t} selected={t.id === selectedTileId} tempPos={{ left: 0, top: 0 }} />
          </div>
        );
      })}
    </div>
  );
};

export default LastTileCont;
